import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { checkAndSetStats, isMobileUserAgent } from "../helpers/helpers";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";
import { Sidebar } from "primereact/sidebar";
import ContactMeBlock from "../components/ContactMeBlock";

// Hook for scroll-triggered animations
const useScrollAnimation = (threshold = 0.1) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold, rootMargin: "0px 0px -50px 0px" }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [threshold]);

    return [ref, isVisible];
};

// Animated section wrapper
const AnimatedSection = ({ children, className = "", delay = 0, animation = "fadeUp" }) => {
    const [ref, isVisible] = useScrollAnimation();

    const animationClass = animation === "fadeUp"
        ? "scroll-animate"
        : animation === "scale"
        ? "scroll-animate-scale"
        : "scroll-animate";

    return (
        <div
            ref={ref}
            className={`${animationClass} ${isVisible ? "is-visible" : ""} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

// Counter animation component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);
    const [ref, isVisible] = useScrollAnimation(0.5);
    const hasAnimated = useRef(false);

    useEffect(() => {
        if (isVisible && !hasAnimated.current) {
            hasAnimated.current = true;
            const startTime = Date.now();
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeOut = 1 - Math.pow(1 - progress, 3);
                setCount(Math.floor(end * easeOut));
                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };
            requestAnimationFrame(animate);
        }
    }, [isVisible, end, duration]);

    return <span ref={ref}>{count}{suffix}</span>;
};

// Team member card
const TeamMemberCard = ({ name, role, delay }) => {
    return (
        <AnimatedSection delay={delay} className="group">
            <div className="bg-neutral-100 rounded-lg p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-2">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-brand-gold to-brand-gold-hover flex items-center justify-center">
                    <span className="text-white text-3xl font-light">
                        {name.split(" ").map(n => n[0]).join("")}
                    </span>
                </div>
                <h3 className="text-xl font-medium text-neutral-800 text-center mb-2">{name}</h3>
                <p className="text-neutral-500 text-center text-sm">{role}</p>
            </div>
        </AnimatedSection>
    );
};

// Value card component
const ValueCard = ({ icon, title, description, delay }) => {
    return (
        <AnimatedSection delay={delay}>
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-neutral-100">
                <div className="w-14 h-14 rounded-full bg-brand-gold-light flex items-center justify-center mb-6">
                    <span className="text-2xl">{icon}</span>
                </div>
                <h3 className="text-xl font-medium text-neutral-800 mb-3">{title}</h3>
                <p className="text-neutral-500 leading-relaxed">{description}</p>
            </div>
        </AnimatedSection>
    );
};

function AboutUs() {
    const doesStatsSent = useRef(false);
    const [contactVisible, setContactVisible] = useState(false);
    const isMobile = isMobileUserAgent();

    useLayoutEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    useEffect(() => {
        document.body.style.backgroundColor = "#FFFFFF";
    }, []);

    useEffect(() => {
        if (!doesStatsSent.current) {
            checkAndSetStats("aboutUsPage");
            doesStatsSent.current = true;
        }
    }, []);

    const stats = [
        { value: 30, suffix: "+", label: "Lorem ipsum dolor" },
        { value: 5000, suffix: "+", label: "Consectetur adipiscing" },
        { value: 50, suffix: "+", label: "Sed do eiusmod" },
        { value: 98, suffix: "%", label: "Tempor incididunt" },
    ];

    const values = [
        {
            icon: "✦",
            title: "Lorem Ipsum",
            description: "Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium.",
        },
        {
            icon: "◇",
            title: "Dolor Sit Amet",
            description: "Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit consequatur.",
        },
        {
            icon: "○",
            title: "Consectetur Adipiscing",
            description: "Neque porro quisquam est qui dolorem ipsum quia dolor sit amet consectetur adipisci.",
        },
    ];

    const team = [
        { name: "Lorem Ipsum", role: "Dolor Sit Amet" },
        { name: "Consectetur Elit", role: "Sed Eiusmod" },
        { name: "Tempor Incididunt", role: "Labore Dolore" },
        { name: "Magna Aliqua", role: "Ut Enim Minim" },
    ];

    const timeline = [
        { year: "2010", title: "Lorem Ipsum Dolor", description: "Ut enim ad minim veniam quis nostrud exercitation ullamco." },
        { year: "2015", title: "Sit Amet Consectetur", description: "Duis aute irure dolor in reprehenderit in voluptate velit." },
        { year: "2020", title: "Adipiscing Elit Sed", description: "Excepteur sint occaecat cupidatat non proident sunt in culpa." },
        { year: "2024", title: "Do Eiusmod Tempor", description: "Sunt in culpa qui officia deserunt mollit anim id est laborum." },
    ];

    return (
        <div className="bg-white min-h-screen">
            <SEO
                title="Про нас"
                description="Дізнайтеся більше про бренд «Цацки» та Харківську ювелірну фабрику — наші цінності, історію та команду майстрів."
                url="/about-us"
            />
            <Navigation variant="light" />

            <main role="main">
                {/* Hero Section */}
                <header className="relative min-h-[70vh] lg:min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 overflow-hidden">
                    {/* Animated background elements */}
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl animate-pulse" />
                        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-brand-gold/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
                    </div>

                    <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
                        <h1 className="opacity-0 animate-fade-in-up text-white text-5xl lg:text-7xl font-light tracking-tight mb-6">
                            Lorem Ipsum
                            <span className="block text-brand-gold mt-2">Dolor Sit Amet</span>
                        </h1>
                        <p className="opacity-0 animate-fade-in-up animate-delay-200 text-white/70 text-xl lg:text-2xl font-light leading-relaxed max-w-2xl mx-auto">
                            Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium totam rem aperiam.
                        </p>
                    </div>

                    {/* Scroll indicator */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-0 animate-fade-in-up animate-delay-400">
                        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-2">
                            <div className="w-1 h-2 bg-white/50 rounded-full animate-bounce" />
                        </div>
                    </div>
                </header>

                {/* Stats Section */}
                <section className="py-16 lg:py-24 bg-neutral-50">
                    <div className="max-w-6xl mx-auto px-6">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                            {stats.map((stat, index) => (
                                <AnimatedSection key={index} delay={index * 100} className="text-center">
                                    <div className="text-4xl lg:text-5xl font-light text-brand-gold mb-2">
                                        <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                                    </div>
                                    <p className="text-neutral-500 text-sm lg:text-base">{stat.label}</p>
                                </AnimatedSection>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Story Section */}
                <section className="py-20 lg:py-32 px-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                            <div>
                                <AnimatedSection>
                                    <span className="text-brand-gold text-sm font-medium tracking-widest uppercase mb-4 block">
                                        Lorem Ipsum
                                    </span>
                                </AnimatedSection>
                                <AnimatedSection delay={100}>
                                    <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 mb-8 tracking-tight">
                                        Dolor Sit Amet Consectetur
                                    </h2>
                                </AnimatedSection>
                                <AnimatedSection delay={200}>
                                    <div className="space-y-6 text-neutral-600 leading-relaxed">
                                        <p>
                                            Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
                                        </p>
                                        <p>
                                            Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.
                                        </p>
                                        <p>
                                            Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis.
                                        </p>
                                    </div>
                                </AnimatedSection>
                            </div>
                            <AnimatedSection delay={300} animation="scale">
                                <div className="relative">
                                    <div className="aspect-square bg-gradient-to-br from-brand-gold/20 to-brand-gold/5 rounded-2xl flex items-center justify-center">
                                        <div className="text-brand-gold text-9xl font-light opacity-30">✦</div>
                                    </div>
                                    <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-brand-gold rounded-xl -z-10" />
                                </div>
                            </AnimatedSection>
                        </div>
                    </div>
                </section>

                {/* Values Section */}
                <section className="py-20 lg:py-32 bg-neutral-50 px-6">
                    <div className="max-w-6xl mx-auto">
                        <AnimatedSection className="text-center mb-16">
                            <span className="text-brand-gold text-sm font-medium tracking-widest uppercase mb-4 block">
                                Adipiscing Elit
                            </span>
                            <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 tracking-tight">
                                Sed Do Eiusmod Tempor
                            </h2>
                        </AnimatedSection>

                        <div className="grid md:grid-cols-3 gap-8">
                            {values.map((value, index) => (
                                <ValueCard
                                    key={index}
                                    icon={value.icon}
                                    title={value.title}
                                    description={value.description}
                                    delay={index * 100}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Timeline Section */}
                <section className="py-20 lg:py-32 px-6">
                    <div className="max-w-4xl mx-auto">
                        <AnimatedSection className="text-center mb-16">
                            <span className="text-brand-gold text-sm font-medium tracking-widest uppercase mb-4 block">
                                Incididunt Ut
                            </span>
                            <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 tracking-tight">
                                Labore Et Dolore
                            </h2>
                        </AnimatedSection>

                        <div className="relative">
                            {/* Timeline line */}
                            <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px bg-neutral-200 -translate-x-1/2" />

                            {timeline.map((item, index) => (
                                <AnimatedSection key={index} delay={index * 150}>
                                    <div className={`relative flex items-center mb-12 ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"}`}>
                                        {/* Timeline dot */}
                                        <div className="absolute left-8 lg:left-1/2 w-4 h-4 bg-brand-gold rounded-full -translate-x-1/2 z-10 ring-4 ring-white" />

                                        {/* Content */}
                                        <div className={`ml-20 lg:ml-0 lg:w-1/2 ${index % 2 === 0 ? "lg:pr-16 lg:text-right" : "lg:pl-16"}`}>
                                            <span className="text-brand-gold font-medium text-lg">{item.year}</span>
                                            <h3 className="text-xl font-medium text-neutral-800 mt-1 mb-2">{item.title}</h3>
                                            <p className="text-neutral-500">{item.description}</p>
                                        </div>
                                    </div>
                                </AnimatedSection>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Team Section */}
                <section className="py-20 lg:py-32 bg-neutral-50 px-6">
                    <div className="max-w-6xl mx-auto">
                        <AnimatedSection className="text-center mb-16">
                            <span className="text-brand-gold text-sm font-medium tracking-widest uppercase mb-4 block">
                                Magna Aliqua
                            </span>
                            <h2 className="text-4xl lg:text-5xl font-light text-neutral-800 tracking-tight">
                                Ut Enim Ad Minim
                            </h2>
                        </AnimatedSection>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                            {team.map((member, index) => (
                                <TeamMemberCard
                                    key={index}
                                    name={member.name}
                                    role={member.role}
                                    delay={index * 100}
                                />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact Us Section */}
                <section className="py-20 lg:py-32 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 px-6 relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-gold/5 rounded-full blur-3xl" />
                    </div>

                    <div className="max-w-4xl mx-auto text-center relative z-10">
                        <AnimatedSection>
                            <h2 className="text-3xl lg:text-5xl font-light text-white mb-6 tracking-tight">
                                Маєте додаткові питання?
                            </h2>
                        </AnimatedSection>
                        <AnimatedSection delay={100}>
                            <p className="text-white/70 text-lg lg:text-xl mb-10 max-w-2xl mx-auto">
                                Залиште свої контактні дані і ми зв'яжемося з вами найближчим часом для консультації.
                            </p>
                        </AnimatedSection>
                        <AnimatedSection delay={200}>
                            <button
                                onClick={() => setContactVisible(true)}
                                className="inline-block btn-animate bg-brand-gold hover:bg-brand-gold-hover text-white px-12 py-5 rounded-sm text-lg font-medium tracking-wide uppercase transition-colors cursor-pointer"
                            >
                                Зв'яжіться зі мною
                            </button>
                        </AnimatedSection>
                    </div>
                </section>
            </main>

            {/* Contact Sidebar */}
            <Sidebar
                className={isMobile ? "w-full" : "w-1/2"}
                visible={contactVisible}
                position="right"
                onHide={() => setContactVisible(false)}
            >
                <ContactMeBlock />
            </Sidebar>

            <Footer containerClassNames="bg-white text-vidrodzhennya" />
        </div>
    );
}

export default AboutUs;
